/* eslint-disable max-params */
import { debug } from './debug';
import type { Navigate, Options } from './defs';
import { error } from './errors';
import { renderQueryString } from './url-encoding';
import { getDefaultThrottle } from './utils';

export const FLUSH_RATE_LIMIT_MS = getDefaultThrottle();

type UpdateMap = Map<string, string | null>;
const updateQueue: UpdateMap = new Map();
const queueOptions: Required<
  Omit<Options, 'startTransition' | 'clearOnDefault'>
> = {
  history: 'replace',
  scroll: false,
  throttleMs: FLUSH_RATE_LIMIT_MS,
};

let lastFlushTimestamp = 0;
let flushPromiseCache: Promise<URLSearchParams> | null = null;

export function enqueueQueryStringUpdate<Value>(
  key: string,
  value: Value | null,
  serialize: (value: Value) => string,
  options: Options
) {
  const serializedOrNull = value === null ? null : serialize(value);
  debug('[nuqs queue] Enqueueing %s=%s %O', key, serializedOrNull, options);
  updateQueue.set(key, serializedOrNull);
  // Any item can override an option for the whole batch of updates
  if (options.history === 'push') {
    queueOptions.history = 'push';
  }
  if (options.scroll) {
    queueOptions.scroll = true;
  }
  queueOptions.throttleMs = Math.max(
    options.throttleMs ?? FLUSH_RATE_LIMIT_MS,
    Number.isFinite(queueOptions.throttleMs) ? queueOptions.throttleMs : 0
  );
}

export function getQueuedValue(key: string) {
  return updateQueue.get(key) ?? null;
}

/**
 * Eventually flush the update queue to the URL query string.
 *
 * This takes care of throttling to avoid hitting browsers limits
 * on calls to the history pushState/replaceState APIs, and defers
 * the call so that individual query state updates can be batched
 * when running in the same event loop tick.
 *
 * @returns a Promise to the URLSearchParams that have been applied.
 */
export function scheduleFlushToURL(navigate: Navigate) {
  if (flushPromiseCache === null) {
    flushPromiseCache = new Promise<URLSearchParams>((resolve, reject) => {
      if (!Number.isFinite(queueOptions.throttleMs)) {
        debug('[nuqs queue] Skipping flush due to throttleMs=Infinity');
        resolve(new URLSearchParams(location.search));
        // Let the promise be returned before clearing the cached value
        setTimeout(() => {
          flushPromiseCache = null;
        }, 0);
        return;
      }
      function flushNow() {
        lastFlushTimestamp = performance.now();
        const [search, error] = flushUpdateQueue(navigate);
        if (error === null) {
          resolve(search);
        } else {
          reject(search);
        }
        flushPromiseCache = null;
      }
      // We run the logic on the next event loop tick to allow
      // multiple query updates to set their own throttleMs value.
      function runOnNextTick() {
        const now = performance.now();
        const timeSinceLastFlush = now - lastFlushTimestamp;
        const throttleMs = queueOptions.throttleMs;
        const flushInMs = Math.max(
          0,
          Math.min(throttleMs, throttleMs - timeSinceLastFlush)
        );
        debug(
          '[nuqs queue] Scheduling flush in %f ms. Throttled at %f ms',
          flushInMs,
          throttleMs
        );
        if (flushInMs === 0) {
          // Since we're already in the "next tick" from queued updates,
          // no need to do setTimeout(0) here.
          flushNow();
        } else {
          setTimeout(flushNow, flushInMs);
        }
      }
      setTimeout(runOnNextTick, 0);
    });
  }
  return flushPromiseCache;
}

function flushUpdateQueue(router: Navigate): [URLSearchParams, null | unknown] {
  const search = new URLSearchParams(location.search);
  if (updateQueue.size === 0) {
    return [search, null];
  }
  // Work on a copy and clear the queue immediately
  const items = Array.from(updateQueue.entries());
  const options = { ...queueOptions };
  // Restore defaults
  updateQueue.clear();
  queueOptions.history = 'replace';
  queueOptions.scroll = false;
  queueOptions.throttleMs = FLUSH_RATE_LIMIT_MS;
  debug('[nuqs queue] Flushing queue %O with options %O', items, options);
  for (const [key, value] of items) {
    if (value === null) {
      search.delete(key);
    } else {
      search.set(key, value);
    }
  }
  try {
    const url = renderURL(location.pathname, search);
    debug('[nuqs queue] Updating url: %s', url);
    router(url, {
      replace: options.history === 'replace',
      preventScrollReset: !options.scroll,
    });
    if (options.scroll) {
      window.scrollTo(0, 0);
    }
    return [search, null];
  } catch (err) {
    console.error(error(429), items.map(([key]) => key).join(), err);
    return [search, err];
  }
}

function renderURL(base: string, search: URLSearchParams) {
  const hashlessBase = base.split('#')[0] ?? '';
  const query = renderQueryString(search);
  const hash = location.hash;
  return hashlessBase + query + hash;
}
